import React, { useState } from 'react';
import { Trash2, Plus, X, ChevronDown, ChevronUp, HelpCircle, Info } from 'lucide-react';
import ColorSwatchPicker from './ColorSwatchPicker';

interface FAQ {
  id: string;
  question: string;
  answer: string;
}

interface FAQBlockData {
  faqs: FAQ[];
  accentColor?: string;
}

const DEFAULT_ACCENT_COLOR = '#f97316';

interface FAQBlockProps {
  data: FAQBlockData;
  onChange: (data: FAQBlockData) => void;
  onDelete: () => void;
  isPreview?: boolean;
}

const FAQBlock: React.FC<FAQBlockProps> = ({ data, onChange, onDelete, isPreview }) => {
  const [openFAQ, setOpenFAQ] = useState<string | null>(null);

  const addFAQ = () => {
    const newFAQ: FAQ = {
      id: Date.now().toString(),
      question: '',
      answer: ''
    };
    onChange({ ...data, faqs: [...data.faqs, newFAQ] });
  };

  const updateFAQ = (id: string, field: keyof FAQ, value: string) => {
    const updatedFAQs = data.faqs.map(faq =>
      faq.id === id ? { ...faq, [field]: value } : faq
    );
    onChange({ ...data, faqs: updatedFAQs });
  };

  const deleteFAQ = (id: string) => {
    onChange({ ...data, faqs: data.faqs.filter(faq => faq.id !== id) });
  };

  const accentColor = data.accentColor || DEFAULT_ACCENT_COLOR;

  if (isPreview) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 justify-center mb-6">
          <HelpCircle className="w-6 h-6" style={{ color: accentColor }} />
          <h3 className="text-2xl font-bold text-gray-900">คำถามที่พบบ่อย</h3>
        </div>
        <div className="space-y-3">
          {data.faqs.map((faq) => (
            <div key={faq.id} className="border border-gray-200 rounded-lg overflow-hidden bg-white">
              <button
                onClick={() => setOpenFAQ(openFAQ === faq.id ? null : faq.id)}
                className="w-full px-6 py-4 flex justify-between items-center hover:bg-gray-50 transition-colors"
              >
                <span className="font-semibold text-gray-900 text-left">{faq.question}</span>
                {openFAQ === faq.id ? (
                  <ChevronUp className="w-5 h-5 text-gray-500 flex-shrink-0" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0" />
                )}
              </button>
              {openFAQ === faq.id && (
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                  <p className="text-gray-600">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="border border-gray-200 rounded-lg p-6 bg-white">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-gray-900">คำถามที่พบบ่อย (FAQ)</h3>
          <div className="relative">
            <Info className="w-4 h-4 text-gray-400 cursor-help peer" />
            <div className="absolute left-0 top-6 w-64 bg-gray-900 text-white text-xs rounded-lg p-3 opacity-0 invisible peer-hover:opacity-100 peer-hover:visible transition-all duration-200 z-10 shadow-xl pointer-events-none">
              <p className="font-semibold mb-1">ตอบคำถามที่ลูกค้ามักถาม</p>
              <p className="text-gray-300">เพิ่มคำถามและคำตอบที่ลูกค้ามักถามบ่อย ช่วยลดข้อสงสัยและเพิ่มความมั่นใจในการตัดสินใจซื้อ</p>
            </div>
          </div>
        </div>
        <button
          onClick={onDelete}
          className="text-red-500 hover:text-red-700 p-2 hover:bg-red-50 rounded-lg transition-colors"
        >
          <Trash2 className="w-5 h-5" />
        </button>
      </div>

      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <ColorSwatchPicker
            compact
            color={accentColor}
            fallback={DEFAULT_ACCENT_COLOR}
            onChange={(hex) => onChange({ ...data, accentColor: hex })}
          />
          <span className="text-sm font-medium text-gray-700">สีไอคอนหัวข้อ</span>
        </div>

        {data.faqs.map((faq, index) => (
          <div key={faq.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
            <div className="flex justify-between items-start mb-3">
              <span className="text-sm font-medium text-gray-700">คำถาม #{index + 1}</span>
              <button
                onClick={() => deleteFAQ(faq.id)}
                className="text-red-500 hover:text-red-700 p-1"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">คำถาม</label>
                <input
                  type="text"
                  value={faq.question}
                  onChange={(e) => updateFAQ(faq.id, 'question', e.target.value)}
                  placeholder="จัดส่งนานไหม?"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">คำตอบ</label>
                <textarea
                  value={faq.answer}
                  onChange={(e) => updateFAQ(faq.id, 'answer', e.target.value)}
                  placeholder="จัดส่งภายใน 1-2 วันทำการครับ"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                />
              </div>
            </div>
          </div>
        ))}

        <button
          onClick={addFAQ}
          className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-orange-500 hover:text-orange-500 transition-colors flex items-center justify-center gap-2"
        >
          <Plus className="w-5 h-5" />
          เพิ่มคำถาม
        </button>
      </div>
    </div>
  );
};

export default FAQBlock;
